import { useSWR } from '@tiger/swr';
import { Button, Form, Input, Skeleton } from 'antd';
import { debounce } from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import { EasyField } from 'react-antd-formutil';
import { useLocation } from 'react-router-dom';
import Card, { CardButtonWrap } from 'components/Card';
import Debug from 'components/Debug';
import FieldSelect from 'components/FieldSelect';
import FormItem from 'components/FormItem';
import ScrollTop from 'components/ScrollTop';
import MarketTag from 'modules/Transfer/components/MarketTag';
import RadioSelect from 'modules/Transfer/components/RadioSelect';
import { SymbolInfo, TransferAssetFormData } from 'modules/Transfer/type';
import withTransferForm, { PageIdEnum, TransferFormProps } from 'modules/Transfer/withTransferForm';
import API from 'utils/API';
import { PARTIAL_ACATS_BASE_PATH } from '../../helper';
import { PartialDetailsPageFormutil } from '../../type';
import { stockLongShortOptions, symbolLabelMap, symbolPlaceholderMap, titleMap } from '../helper';
import style from '../style.scss';

const FillStock: FC<TransferFormProps<TransferAssetFormData>> = ({
    $formutil,
    getStorageDataByKey,
    onSubmit,
    setStorageDataByKey,
    goNextPath
}) => {
    const location = useLocation();
    const locationState = location.state as { isEdit: boolean; editStockName: string } | undefined;
    const { isEdit, editStockName } = locationState || {};
    // 点进添加股票/etf页面的时候, partialDetails页面可能还没有缓存的值
    const partialDetailsData = getStorageDataByKey<PartialDetailsPageFormutil | undefined>(PageIdEnum.PARTIAL_DETAILS);
    const { temporaryStockList } = partialDetailsData || {};
    const { $params, $setValues } = $formutil;
    const { symbol: formSymbol } = $params;
    // 搜索的股票关键字
    const [searchStockKeyword, setSearchStockKeyword] = useState('');
    // 股票和etf搜索, 期权不支持行情查询
    const { data: symbolListData, isLoading: symbolListLoading } = useSWR<SymbolInfo[]>(
        !!searchStockKeyword && {
            url: API.HQ.search(),
            params: {
                limit: 10, // 结果条目限制
                // 产品确认, 市场只用美国市场, 语言只需要英语
                market: 'US', // 市场列表
                lang: 'en_US',
                withFund: false, // 是否包含了基金
                withPlate: false, // 是否包含版块
                // type是否区分股票和etf, 如果不区分, 股票和etf的type都是0; 如果区分, 股票的type是0, etf的type是12
                supportEtfType: true,
                forceInsertFund: false, // 是否在股票的搜索结果强制插入基金
                withWarrantChain: false, // 是否包含涡轮牛熊链表
                withPortfolioFund: false, // 是否包括组合基金
                keyword: searchStockKeyword,
                // 只有股票/etf有搜索的功能, 期权不支持搜索查询
                type: '0,12' // 0: 股票, 12: ETF
            }
        },
        {
            dataChain: resp => resp.items
        }
    );
    const symbolList = useMemo<
        Array<{ label: string; value: string; disabled: boolean; extraLabel: React.ReactNode; symbol: string }>
    >(() => {
        if (!symbolListData) {
            return [];
        }

        return symbolListData.map(symbolItem => {
            // 已退市
            const isDelist = symbolItem.halted === 4;
            let isSelected = false;

            // 如果当前项已经存在于已经选中的股票列表中, 则禁用
            if (temporaryStockList?.find(item => item.symbol === symbolItem.symbol)) {
                isSelected = true;
            }

            return {
                symbol: symbolItem.symbol,
                label: symbolItem.nameCN,
                disabled: isDelist || isSelected,
                extraLabel: (
                    <div>
                        <span>
                            {symbolItem.nameCN}
                            {isDelist && ` (${__('退市')})`}
                        </span>
                        <div>
                            <MarketTag market={symbolItem.market} /> {symbolItem.symbol}
                        </div>
                    </div>
                ),
                value: `${symbolItem.symbol} ${symbolItem.nameCN}`
            };
        });
    }, [symbolListData, temporaryStockList]);
    const debounceFn = useMemo(() => debounce(fn => fn(), 300), []);
    const handleStockSearch = (symbol: string) => {
        debounceFn(() => {
            setSearchStockKeyword(symbol);
        });
    };
    const handleStockChange = (symbol: string) => {
        const symbolItem = symbolList.find(item => item.value === symbol);

        if (symbolItem) {
            $setValues({ symbol_name: symbolItem.label, symbol: symbolItem.symbol });
        }
    };
    const handleDelete = () => {
        onSubmit(() => {
            // 需要更新partial-details的temporaryStockList
            setStorageDataByKey(PageIdEnum.PARTIAL_DETAILS, {
                ...partialDetailsData,
                temporaryStockList: temporaryStockList?.filter(item => item.symbol !== formSymbol) || []
            });

            goNextPath('../partial-details');
        });
    };
    const handleSubmit = () => {
        onSubmit(() => {
            if (isEdit && temporaryStockList) {
                // 需要更新partial-details的temporaryStockList
                setStorageDataByKey(PageIdEnum.PARTIAL_DETAILS, {
                    ...partialDetailsData,
                    /**
                     * 分为以下几种可能得情况:
                     * 1. 如果操作的是当前股票, 则将编辑的股票项替换成当前表单项
                     * 2. 如果修改成了新的股票, 则将编辑的股票项替换成当前表单项
                     * 3. 这里不能改成已经存在的非当前股票, 会禁止选中
                     */
                    // 找到列表中的当前编辑的股票项, 然后替换成表单项
                    temporaryStockList: temporaryStockList.map(item => (item.symbol === editStockName ? $params : item))
                });
            } else {
                // 需要更新partial-details的temporaryStockList
                setStorageDataByKey(PageIdEnum.PARTIAL_DETAILS, {
                    ...partialDetailsData,
                    // temporaryStockList可能不存在, 也可能已经有值
                    temporaryStockList: (temporaryStockList || []).concat($params)
                });
            }

            goNextPath('../partial-details');
        });
    };

    useEffect(() => {
        if (isEdit && temporaryStockList) {
            const symbolData = temporaryStockList.find(item => item.symbol === editStockName);

            if (symbolData) {
                $setValues(symbolData);
            }
        }
    }, [isEdit, editStockName, temporaryStockList, $setValues]);

    return (
        <Card form>
            <ScrollTop />
            <h3 className="transfer-title">{titleMap.stock}</h3>
            <Form noValidate layout="vertical" onFinish={handleSubmit}>
                <FormItem
                    required
                    name="temporarySymbol"
                    label={symbolLabelMap.stock}
                    $onFieldChange={handleStockChange}
                    validMessage={{ required: symbolPlaceholderMap.stock }}>
                    <FieldSelect
                        showSearch
                        optionLabelProp="value"
                        onSearch={handleStockSearch}
                        notFoundContent={
                            symbolListLoading ? (
                                <Skeleton active title={false} paragraph={{ rows: 3, width: '100%' }} />
                            ) : searchStockKeyword ? (
                                __('暂无数据')
                            ) : (
                                symbolPlaceholderMap.stock
                            )
                        }
                        placeholder={symbolPlaceholderMap.stock}
                        data={symbolList}
                    />
                </FormItem>
                <EasyField name="symbol_name" type="hidden" />
                <EasyField name="symbol" type="hidden" />
                {/* 这个字段是为了传给后端, 方便后端进行数据处理, 用于区分是股票还是期权 */}
                <EasyField name="secType" type="hidden" $defaultValue="STOCK" />
                <RadioSelect
                    label={<div className={style.boldLabel}>{__('Long/Short')}</div>}
                    name="long_short"
                    options={stockLongShortOptions}
                    $defaultValue="long"
                />
                <FormItem
                    required
                    name="quantity"
                    label={__('Transfer:Quantity')}
                    className={style.numberInputItem}
                    validMessage={{ required: __('Please input quantity') }}
                    getValueFromEvent={({ target }) => target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '')}>
                    <Input placeholder={__('Enter')} autoComplete="off" />
                </FormItem>
                <CardButtonWrap className="mt-5">
                    <Button size="large" onClick={isEdit ? handleDelete : () => goNextPath('../partial-details')}>
                        {isEdit ? __('Delete') : __('Back')}
                    </Button>
                    <Button type="primary" size="large" htmlType="submit">
                        {isEdit ? __('Save') : __('Add List')}
                    </Button>
                </CardButtonWrap>
                <Debug data={$params} />
            </Form>
        </Card>
    );
};

export default withTransferForm({
    id: PageIdEnum.FILL_STOCK,
    step: 3,
    basePath: PARTIAL_ACATS_BASE_PATH,
    // 这个页面的内容只是为了操作表单, 甚至可以理解为一个弹窗, 不需要缓存表单值
    cache: false,
    /**
     * 使用PageIdEnum.BROKER/PageIdEnum.CHECK来判断是否是手敲链接的误入
     * 注意这里不能加PageIdEnum.PARTIAL_DETAILS
     * 因为首次进入的时候缓存中还没有PageIdEnum.PARTIAL_DETAILS的值
     */
    ensure: [PageIdEnum.BROKER, PageIdEnum.CHECK]
})(FillStock);
